/**
 * DEFUSE S10 (defuse-core) — typed Block-INSERT flatten.
 *
 * Typed counterpart of the legacy `scene3d/blockInsert.ts`
 * `flattenBlockInsert` / `flattenBlockInsertEntity` + bag amplifier
 * `blockChildToEntityPreview`. Flattens one `BlockInsertEntity` into a list
 * of `RenderableEntity` by resolving the block definition, transforming each
 * typed child (`blockTransform.ts` — legacy-affine baseline) and routing the
 * transformed `BlockChildEntity` DIRECTLY through `entityToRenderableTyped`
 * (plan §4.1 caveat: block children carry synthetic ids that never resolve
 * via `getEntityById`; §4.6 integration: leaf children go to the dispatcher
 * as typed entities, never through the string bag).
 *
 * NOT a dispatcher case — one INSERT produces MANY renderables (plan §4.6
 * integration note); `entityToRenderableTyped(case "Block")` stays `null`.
 * The legacy flatten remains live and untouched: deletion + caller rewiring
 * are coupled into the S11 cutover (plan §4.3 cycle-S10fix-r2
 * deletion-coupling; golden-parity-green is that cutover's precondition).
 *
 * # Two-phase child walk (plan §4.6, cycle-S10fix-r2/r3; S10c LANDED)
 *
 *   Phase A — ATTRIB value resolution (S10c — LANDED, Lane F/D2): resolve
 *     `insert.attributes[]` against block ATTDEFs BY TAG and render the
 *     substituted VALUES via `attdefFromEntity` (see that module's header for
 *     the full Phase A/B contract — value/placement/visibility resolution,
 *     the multiline fidelity boundary, and the reference sources checked).
 *   Phase B — generic dispatch with two carve-outs:
 *     (1) ATTDEF children route through `attdefFromEntity` (S10c — LANDED):
 *         a non-constant ATTDEF never renders its OWN tag standalone
 *         (AutoCAD renders the ATTRIB value, not the tag; plan §4.6
 *         inversion) — Phase A substitutes the per-INSERT ATTRIB value. A
 *         constant ATTDEF (`flags.constant`) always renders its own
 *         `defaultValue` — Phase B.
 *     (2) nested `Block` children RECURSE the flatten with the accumulated
 *         transform (the dispatcher would return null and silently drop
 *         them — plan §4.6 carve-out 2), mirroring the legacy recursion
 *         (`blockInsert.ts:734-796`) incl. the linetype/48-chain threading
 *         AND the nested Block's OWN `attributes[]` (a nested INSERT may
 *         itself carry ATTRIBs — matches `insertConverter.ts`'s PDF-plot
 *         precedent).
 *
 * # S10 baseline vs deferred forward-fix arms (plan §6 rows)
 *
 *   - MINSERT `columnCount/rowCount/columnSpacing/rowSpacing` — LANDED (S10a,
 *     Lane F/D2): `blockFromEntity` expands the grid at the TOP level only
 *     (see that function's doc comment — MINSERT cannot legally nest inside a
 *     block definition, matching `insertConverter.ts`'s PDF-plot precedent,
 *     which likewise never grid-expands a nested `Block` child).
 *   - Per-leaf `child.extrusion` OCS→WCS — NOT applied (adapters are
 *     WCS-only by the r7 ownership decision). Owned by S10b-EXT / S10-XFORM.
 *   - Annotative filter (`annotationScales` / `ctx.annoAllVisible`) — S10d.
 *   - Dynamic-block visibility via `evaluateBlock` — LANDED (S10e, N1). See
 *     "Dynamic-block evaluation" below for the full contract.
 *   - Full Mat4 (3D rotation, non-uniform Circle/Arc promotion) — S10-XFORM.
 *
 * # Dynamic-block evaluation (S10e, N1 — LANDED)
 *
 * BEFORE this landed, Phase A/B walked `blockDef.entities` (the STATIC,
 * unevaluated block content) directly — a dynamic block with 2+ visibility
 * states rendered EVERY state superimposed (the legacy SVG `blockRender.tsx`
 * `getCachedEvaluation` / `blockInstanceResolve.ts` `resolveNestedBlockChild`
 * already called `evaluateBlock` correctly; only this typed flatten path
 * bypassed it). `resolveEvaluatedChildren` (helper below) is now the ONE
 * place a `BlockDefinition` at any recursion level is turned into its
 * PER-INSTANCE resolved entity list, called ONCE per `flattenTypedInsert`
 * invocation (top-level INSERT, or a nested `Block` child's own recursion —
 * `evaluateBlock` does NOT recurse into nested INSERTs itself, by design;
 * `evaluatorBatchC.test.ts` "passes blockDefinitions through to recursive
 * evaluation" pins this — "Actual cascade evaluation is visible at the
 * renderer layer", i.e. the CALLER re-invokes `evaluateBlock` per nesting
 * level, exactly what `flattenTypedInsert`'s pre-existing recursive
 * structure already does). Both Phase A and Phase B walk the SAME evaluated
 * list — required, not incidental: a Visibility-state-hidden ATTDEF must
 * stop rendering its substituted ATTRIB value too, which only holds if both
 * phases see the identical filtered set (a Phase A/B split source would
 * either render an orphaned ATTRIB with no visible ATTDEF, or the reverse).
 *
 *   - Static blocks (`!blockDef.dynamicMetadata`) short-circuit BEFORE
 *     calling the evaluator — `resolveEvaluatedChildren` returns
 *     `blockDef.entities` by reference, byte-identical to pre-S10e output
 *     (golden parity for every non-dynamic fixture; zero added evaluator
 *     cost on the overwhelmingly common case).
 *   - Per-instance state: the top-level entry reads
 *     `insert.customProperties` / `insert.visibilityState` (present on
 *     `blockInsertEntitySchema` since Phase 3 round-trip — model.ts); a
 *     nested `Block` child recursion (Carve-out 2) reads its OWN
 *     `child.customProperties` / `child.visibilityState` (the SAME schema —
 *     a nested INSERT is a full `blockInsertEntitySchema` member of
 *     `blockChildEntitySchema`), matching `blockInstanceResolve.ts`
 *     `resolveNestedBlockChild`'s exact convention. The synthetic
 *     `flattenBlockReference` callers (MLEADER block content, MText
 *     embedded blocks, instanced-INSERT shared geometry) have no per-
 *     reference dynamic state in the typed model — same precedent as their
 *     pre-existing `attributes: []` default — so they evaluate at the
 *     block's AUTHORED DEFAULT state (still strictly better than the
 *     pre-S10e "every state superimposed").
 *   - Occurrence-index precompute: `attdefTagOccurrenceCount` /
 *     `constantAttdefTags` / `staticOccurrenceByChildId` are built from the
 *     FULL, UNFILTERED `blockDef.entities` (not the evaluated/visibility-
 *     filtered list) — `attributes[]` row↔ATTDEF occurrence matching is
 *     fixed at INSERT-attach time against the AUTHORED ATTDEF list,
 *     independent of any one instance's ACTIVE visibility state. Computing
 *     it from the filtered list instead would misalign the occurrence index
 *     whenever a PRECEDING same-tag ATTDEF is hidden in the active state.
 *   - MINSERT grid (S10a) + repeated nested-Block siblings sharing the same
 *     `(blockName, customProperties, visibilityState)` triple: memoised
 *     via `EvalMemo`, a `Map` created ONCE per top-level `blockFromEntity` /
 *     `flattenBlockReference` call and threaded through every recursive
 *     `flattenTypedInsert` — NOT module-global (this file's "No flatten
 *     LRU" decision above stays intact: a fresh `Map` per call holds
 *     nothing across two SEPARATE calls, so
 *     `blockFromEntity.behavior.test.ts` "pure flatten: a layer-linetype
 *     change between calls is reflected immediately" is unaffected — this
 *     memo only dedupes work WITHIN one flatten pass). Key scheme mirrors
 *     `blockRender.tsx`'s `evalCache` (`buildEvaluateBlockCacheKey`, now
 *     shared via `@dxf-ai-editor/block-evaluator` to avoid a jscpd clone)
 *     prefixed with the ancestor block-NAME chain, so two evaluations of
 *     the identical block+instance at genuinely different nesting depths
 *     never collide on `evaluateBlock`'s OWN depth/cycle guard (which reads
 *     `parentChain.length`) — MINSERT cells and sibling nested instances
 *     share an IDENTICAL chain, so the common cases still dedupe.
 *
 * # Deliberate divergences from the legacy flatten (typed reads the model)
 *
 *   - `insertionPoint.z` / `scaleZ`: the legacy PREVIEW producer never emits
 *     `Insertion Z` / `Scale Z` (`buildBlockInsertPreview`), so the live
 *     legacy path always flattens at z=0 / scaleZ=1. The typed path reads
 *     the model fields directly (S-INSERT-INSERTPT-Z ✅#242) — bag-starvation
 *     closed by construction. Golden-parity fixtures use z=0 (plan §4.4:
 *     golden-vs-legacy holds only for the z=0 2D case).
 *   - Coordinate-origin offset: the legacy flatten applies `applyOffset`
 *     TWICE under a locked origin (once per child inside the adapters, once
 *     more on the insertion in `flattenBlockInsertEntity`) — a latent legacy
 *     quirk. The typed path folds the insertion into the child transform
 *     BEFORE dispatch, so the per-kind adapters apply the offset exactly
 *     once. Identical output when no origin is locked (all unit fixtures).
 *   - No flatten LRU (S11 wiring decision): the legacy module-global cache
 *     had NO production invalidation (`clearBlockFlattenCache` had zero
 *     production callers — a block-definition edit re-rendered STALE cached
 *     primitives), and its insertion-agnostic caching contract cannot hold
 *     for typed hatch children (pattern-line origins do not survive a
 *     post-flatten translate). This flatten is deliberately pure and
 *     uncached — deterministic output per call, no staleness class. If the
 *     perf fixtures ever regress on INSERT-heavy drawings, memoise AT THE
 *     CALL SITE per bulk-load pass (never module-global).
 *   - BYBLOCK colour (@codex #431 round-2): the INSERT's resolved colour is
 *     threaded as `ctx.blockColor` (AutoCAD: BYBLOCK = the block reference's
 *     colour; the PDF twin `insertConverter.ts:229/:434` does the same). The
 *     legacy flatten never threads it (`SceneBulkLoad` passes the base ctx —
 *     BYBLOCK children fall back to their layer there), so this is a
 *     legacy-wrong carve-out like the Point location fix: golden-parity
 *     holds for every non-BYBLOCK-coloured child, and the BYBLOCK case is
 *     locked by a superiority test.
 *   - Layer "0" inheritance (@codex #431 round-3): a child authored on layer
 *     "0" inherits the enclosing INSERT's effective layer, propagated through
 *     nested INSERTs (innermost non-"0" wins) — `resolveBlockChildLayer`,
 *     the same rule the EXPLODE reducer (`state/blockReducerHelpers.ts`) and
 *     the PDF twin (`insertConverter.ts` PDF-6.2a) apply. The legacy scene3d
 *     flatten keeps `child.layer` verbatim (wrong visibility/ByLayer bucket)
 *     — legacy-wrong carve-out; golden fixtures avoid layer-0 children under
 *     non-"0" INSERTs, and the inheritance is locked by superiority tests.
 *
 * LIVE since the S11 render cutover: `scene/SceneBulkLoad.ts` routes every
 * passthrough INSERT through {@link blockFromEntity}, and the synthetic
 * block-reference callers (MLEADER block content
 * `dimensions/multileaderToPrimitives.ts`, MText embedded blocks
 * `text/mtextEmbeddedBlock.ts`, instanced-INSERT shared geometry
 * `adapters/blockInsertInstanced.ts`) flatten through
 * {@link flattenBlockReference}. The legacy `scene3d/blockInsert.ts` is
 * DELETED (its frozen replica lives on as the golden-parity oracle in
 * `__tests__/legacyBlockFlattenOracle.ts`). Deliberately NOT re-exported
 * from `./index` (the barrel hosts `entityToRenderableTyped`, which this
 * module imports — a barrel re-export would create an import cycle,
 * forbidden by the dependency-cruiser zero-cycle gate). Callers import from
 * this module directly.
 */

import {
  lineEntitySchema,
  safeRecordLookup,
} from "@dxf-ai-editor/drawing-model";
import type {
  BlockDefinition,
  BlockInsertAttribute,
  BlockInsertEntity,
  DimensionEntity,
} from "@dxf-ai-editor/drawing-model";

import {
  evaluateBlock,
  buildEvaluateBlockCacheKey,
} from "@dxf-ai-editor/block-evaluator";

// The block-reference LINEAR algebra + the recursion-placeholder geometry are
// ONE truth shared with the block-BOUNDS walk (`cad-core`'s
// `blockLinearTransform.ts` / `recursionPlaceholder.ts`) — Codex PR #784
// round 7 P2. cad-core may not import `apps/web`, so this is the forward edge.
import {
  composeNestedBlockLinear,
  guardBlockScale,
  MAX_BLOCK_RECURSION_DEPTH,
  recursionPlaceholderLabelEntity,
  recursionPlaceholderSquareCorners,
  recursionPlaceholderSquareSize,
} from "@dxf-ai-editor/cad-core";

import type { RenderableEntity, Vec3 } from "../../types";

import type { AdapterContext } from "../adapterContext";

import {
  attdefFromEntity,
  IDENTITY_ATTRIB_OUTER,
  orphanAttributeFromEntity,
  type AttribOuterTransform,
} from "./attdefFromEntity";
import {
  insertAnnotativeHidden,
  resolveTypedAnnotation,
} from "./annotativeResolve";
import {
  explodeBulgedHeavyPolylineChild,
  explodeBulgedLwpolylineChild,
  heavyPolylineNeedsBulgeExplode,
  isMirroredTransform,
  lwpolylineNeedsBulgeExplode,
  blockLinearOfTransform,
  resolveBlockChildLayer,
  applyBlockChildBandFrame,
  applyBlockChildThicknessSweep,
  transformBlockChild,
  transformBlockChildPoint,
  transformBlockDirection,
  transformDimensionPrimitive,
  transformEmittedBounds,
  type BlockInsertTransform,
} from "./blockTransform";
import { renderBlockChildMultileader } from "./blockChildMultileader";
import { renderBlockChildOleFrame } from "./blockChildOleFrame";
import { renderBlockChildTable } from "./blockChildTable";
import { blockReferenceChildContext } from "./blockReferenceContext";
import { resolveInsertLinetypeTyped } from "./insertLinetype";
// Re-exported (not just imported): the resolver MOVED to the leaf module
// `./insertLinetype.ts` (Codex PR #784 round 4 F1 — it had to stop pulling
// `multileaderGeometry.ts` into an import cycle through this file), and the
// barrel re-export keeps every pre-existing import path unchanged.
export { resolveInsertLinetypeTyped };
import type {
  BlockContentFlattener,
  BlockReferenceFlattenArgs,
} from "./blockReferenceFlatten";
import { dimensionFromEntity, entityToRenderableTyped } from "./index";

/**
 * Hard recursion-depth cap — the legacy `MAX_BLOCK_RECURSION_DEPTH`
 * (`blockInsert.ts:103`), now re-exported from `cad-core` (Codex PR #784
 * round 7 P2): the block-BOUNDS walk must predict EXACTLY when this flatten
 * stops descending and draws the placeholder instead, so the cap is one
 * shared number rather than two constants that can drift. (It is still not
 * imported from the legacy module, which S11 deletes.)
 */
export const MAX_TYPED_BLOCK_RECURSION_DEPTH = MAX_BLOCK_RECURSION_DEPTH;

/**
 * Round-2 FINDING 4 — hard upper bound on flattened MINSERT grid cells.
 * `columnCount`/`rowCount` (DXF 70/71) are schema-valid but UNBOUNDED ints
 * (`blockInsertEntitySchema` — the Autodesk DXF reference documents no
 * ceiling for either), and EACH cell runs a FULL recursive block-flatten
 * (`flattenOneCell` below — not a cheap per-instance matrix like the
 * sibling ARRAYRECT path). An imported or hand-edited DXF with an
 * extreme-but-valid grid (e.g. `columnCount: 100000, rowCount: 100000`)
 * would synchronously flatten a proportional number of full block subtrees
 * on the render path — a perf/hang risk with no existing MINSERT-specific
 * guard to reuse: `arrayRectToInstanced.ts` sidesteps the analogous
 * ARRAYRECT risk entirely by construction (ONE `InstancedMesh` + `rows×cols`
 * translation matrices — O(1) geometry, not a per-cell flatten — so it never
 * needed a numeric ceiling; its own doc comment cites a 50×50 = 2 500-
 * instance array as the realistic "large" case). AutoCAD's MINSERT command
 * itself documents no row/column ceiling either, but MINSERT's actual use
 * case — a title-block stamp / parts grid — is low hundreds of cells at
 * most in practice. 100×100 = 10 000 cells is a generous ~4× headroom over
 * that cited realistic-large ARRAYRECT case while still bounding worst-case
 * synchronous work to a fixed budget. Cells beyond the cap are dropped
 * (row-major order, the SAME iteration order the grid loop already uses)
 * with a `console.warn` — never a thrown error (a render adapter must not
 * throw on schema-valid input) — mirroring the "block not found" warn a
 * few lines into `flattenTypedInsert`.
 */
export const MAX_MINSERT_CELLS = 10_000;

/**
 * S10e — per-flatten-call `evaluateBlock` memo. See module doc "Dynamic-block
 * evaluation" for the full rationale (why this is per-call, not module-
 * global, and what the key includes). Created fresh in `blockFromEntity` /
 * `flattenBlockReference` and threaded through every recursive
 * `flattenTypedInsert` call via {@link FlattenTypedInsertArgs.evalMemo}.
 */
type EvalMemo = Map<string, BlockDefinition["entities"]>;

/**
 * S10e — resolve the PER-INSTANCE evaluated entity list for one recursion
 * level. Static blocks (`!blockDef.dynamicMetadata`) short-circuit to
 * `blockDef.entities` BY REFERENCE before touching the evaluator or the
 * memo — byte-identical to pre-S10e output, zero added cost for the common
 * case. Dynamic blocks call `evaluateBlock` (module doc "Dynamic-block
 * evaluation"), memoised by a `[parentChain, blockName|props|vis]` key —
 * `JSON.stringify`-composed (NOT a hand `::`-join; local Codex review, P2,
 * pre-PR — the same delimiter-collision class `buildEvaluateBlockCacheKey`
 * closes internally: an ancestor block NAME containing the literal join
 * character, or an already-JSON-encoded inner key doing the same, could
 * otherwise collide two DIFFERENT (chain, instance) pairs onto the same
 * memo entry) so a MINSERT grid / repeated same-state nested sibling
 * evaluates once.
 */
function resolveEvaluatedChildren(
  blockDef: BlockDefinition,
  blocks: Record<string, BlockDefinition>,
  customProperties: Readonly<Record<string, string | number | boolean>>,
  visibilityState: string | undefined,
  parentChain: readonly string[],
  evalMemo: EvalMemo,
): BlockDefinition["entities"] {
  // KNOWN GAP (local Codex review, P1, pre-PR; NOT introduced by S10e —
  // see module doc "Dynamic-block evaluation — anonymous *U variant gap"
  // for the full investigation) — `blockDef` here is `blocks[blockName]`
  // resolved from the INSERT's OWN `blockName`. For a REAL AutoCAD-
  // exported dynamic block placed at a non-default state, AutoCAD bakes
  // that state's STATIC geometry into an anonymous `*U<N>` BLOCK_RECORD
  // and points the INSERT at `*U<N>`, NOT the named dynamic master — the
  // master (which carries the actual `dynamicMetadata` eval graph) is a
  // SEPARATE, differently-named `BlockDefinition`, linked only via
  // `AcDbDynamicBlockTrueName` XDATA that `attachInsertCustomProperties`
  // (dxfBridge.dynamic.params.ts) resolves ONLY to seed `customProperties`
  // defaults — `entity.blockName` is left pointing at the variant, and the
  // variant→master linkage is NOT preserved anywhere in the persistent
  // typed model. So `blocks["*U<N>"]` genuinely has no `dynamicMetadata`
  // (Track A detection does not flag anonymous variants — see
  // `dxfBridge.dynamic.detect.ts` module doc), and THIS fast path takes
  // the static branch — a DYNSET / customProperties change on such an
  // INSERT never reaches `evaluateBlock`, so its render stays frozen at
  // the imported baked snapshot.
  //
  // Confirmed PRE-EXISTING, not a regression: the legacy SVG path
  // (`scene/blockRender.tsx`'s `getCachedEvaluation`) resolves `blockDef`
  // the IDENTICAL way (`blockDefinitions[geom.blockName]`, the raw/
  // unresolved variant name read off the preview bag), so it has the SAME
  // limitation today, already shipped.
  //
  // NOT fixed here — a correct fix needs the master's `entities` (the
  // action graph's `selectionEntityIds` are authored against the MASTER's
  // own entity ids, not the variant's independently-baked ones — naively
  // borrowing just `dynamicMetadata` onto the variant's OWN entities would
  // silently no-op every action, a WORSE partial-fix failure mode than
  // today's "frozen at baked snapshot") AND a way to reach it from a bare
  // variant name, which requires a NEW persistent-model field carrying the
  // variant→master linkage through import (an S10e-DYNBLOCK-VARIANT-MASTER
  // follow-up — cross-cutting: also affects `blockInsertInstanced.ts`'s
  // `isDynamicBlockInsert`, `blockRender.tsx`, `blockInstanceResolve.ts`).
  if (!blockDef.dynamicMetadata) return blockDef.entities;
  const instance =
    visibilityState !== undefined
      ? { customProperties, visibilityState }
      : { customProperties };
  const key = JSON.stringify([
    parentChain,
    buildEvaluateBlockCacheKey(blockDef.name, instance),
  ]);
  const cached = evalMemo.get(key);
  if (cached) return cached;
  const result = evaluateBlock({
    blockDef,
    instance,
    blockDefinitions: blocks,
    parentChain,
  });
  const entities = result.entities as BlockDefinition["entities"];
  evalMemo.set(key, entities);
  return entities;
}

/**
 * Flatten one typed INSERT into renderables.
 *
 * Signature per plan §4.6 integration —
 * `blockFromEntity(insert, blocks, ctx, visited?, depth?) → RenderableEntity[]`.
 * `visited` / `depth` are internal-recursion parameters (cycle guard chain of
 * block NAMES + current nesting depth); top-level callers omit them.
 *
 * # MINSERT grid (S10a — LANDED, Lane F/D2; cell cap — round-2 FINDING 4)
 *
 * `columnCount`/`rowCount`/`columnSpacing`/`rowSpacing` (DXF 70/71/44/45)
 * expand into `columnCount × rowCount` full flatten passes, ONE PER CELL,
 * at THIS top level only, bounded by `MAX_MINSERT_CELLS` (doc comment on
 * that constant — an extreme-but-schema-valid grid renders only its first
 * `MAX_MINSERT_CELLS` cells, row-major, with a `console.warn`) — matching
 * TWO independent existing
 * implementations in this codebase: the legacy dialog-preview renderer
 * (`scene/blockRender.tsx renderBlockInstance`, `cellMatrix =
 * baseMatrix.translate({x: col·columnSpacing, y: row·rowSpacing})`) and the
 * PDF-plot converter (`print/pdf/converters/insertConverter.ts
 * insertToPrimitives`, `compose(matrix, translationMatrix)`). Both compose
 * the per-cell offset INSIDE the block's local (pre-basePoint-subtract)
 * frame — i.e. the offset rides the SAME rotate+scale as the block content
 * — which is exactly `transformBlockDirection(offset, t)` (a pure linear
 * map, no translation/basePoint subtract) added to `t.insertion`.
 *
 * Grid expansion is deliberately NOT threaded into the recursive
 * `flattenTypedInsert` — a nested `Block` child (Carve-out 2) never grid-
 * expands even if its schema carries a non-1 `columnCount`/`rowCount`,
 * because MINSERT cannot legally nest inside a block definition (AutoCAD:
 * MINSERT is a MODEL/PAPER-space-only insertion), matching
 * `insertConverter.ts`'s `walkBlock` "Block" case, which likewise reads only
 * `insertionPoint`/`scaleX`/`scaleY`/`rotation` for a nested child and never
 * consults its `columnCount`/`rowCount`.
 *
 * ATTRIB substitution applies PER CELL with the SAME `insert.attributes[]`
 * for every cell (`insertConverter.ts`: "emit ATTRIB text runs … PER CELL so
 * a MINSERT grid's stamp text is repeated with the geometry") — AutoCAD's
 * MINSERT command does not support per-cell attribute variation (there is
 * no DXF mechanism for it; a real MINSERT never carries `attributesFollow`
 * in practice), so every cell's ATTDEF/ATTRIB resolution is identical.
 */
export function blockFromEntity(
  insert: BlockInsertEntity,
  blocks: Record<string, BlockDefinition>,
  ctx: AdapterContext,
  visited: ReadonlySet<string> = new Set<string>(),
  depth = 0,
  /**
   * Codex PR #784 round 3 P1 — optional OUT-OF-BAND per-child ancestry sink
   * (see {@link BlockChildAncestry}). When supplied, every renderable emitted
   * INSIDE a nested block reference is recorded under its enclosing reference
   * layers, outermost first, EXCLUDING this top-level INSERT's own layer. The
   * returned renderables are untouched either way, so the flatten's
   * parity-pinned output is identical with and without the sink.
   */
  ancestryOut?: BlockChildAncestry,
): RenderableEntity[] {
  // Annotative visibility gate + top-level transform — ONE truth shared with
  // the cache-direct `block-insert` wrapper (`scene/liftBlockInsert.ts`), see
  // {@link resolveBlockInsertTransform}. A hidden INSERT flattens to zero
  // renderables — mirrors the legacy SVG `blockRender.tsx`'s
  // `if (!annoCtx.isVisible) return null`.
  const transform = resolveBlockInsertTransform(insert, ctx);
  if (!transform) return [];

  // FIX-2 / PR-LT3 parity — thread the INSERT's OWN resolved linetype +
  // group-48 scale down the BYBLOCK chain, exactly like the legacy
  // `flattenBlockInsertEntity:1088-1107`.
  //
  // @codex #431 round-2 — the INSERT's resolved COLOUR is threaded too:
  // `buildRenderableShellTyped` resolves a BYBLOCK-coloured child from
  // `ctx.blockColor` (AutoCAD: BYBLOCK = the colour of the block REFERENCE),
  // so without this the public entry point left BYBLOCK children on their
  // layer fallback. `resolveColorHexFromEntity(insert, …)` covers the whole
  // chain (explicit → its hex; ByLayer → the INSERT's layer hex; ByBlock →
  // the enclosing `ctx.blockColor`). DELIBERATE divergence from the legacy
  // flatten, which never threads colour (`SceneBulkLoad` passes the base ctx
  // straight through — a legacy gap): the PDF twin `insertConverter.ts:229`
  // threads `blockColor: insertHex` exactly like this. Golden-parity holds
  // for every non-BYBLOCK-coloured child (the field is only consulted for
  // `byBlock`); the BYBLOCK case is locked by a superiority test.
  //
  // @codex PR #785 round 2 F2 — the three-field thread is ONE helper
  // ({@link blockReferenceChildContext}) shared with the nested-INSERT arm
  // below and with `./tableFromEntity.ts` (an ACAD_TABLE is a block reference
  // too, and forwarded the ambient context verbatim before that round).
  const childCtx: AdapterContext = blockReferenceChildContext(insert, ctx);

  // S10e — one memo for this ENTIRE top-level call (shared across every
  // MINSERT grid cell below), NOT module-global (module doc "Dynamic-block
  // evaluation"). `Array.from(visited)` mirrors the ancestor chain
  // `evaluateBlock`'s OWN depth/cycle guard expects — `[]` for every real
  // (non-recursive) caller, since `blockFromEntity` is only ever invoked at
  // the true top level in production (nested INSERTs recurse through
  // `flattenTypedInsert`, never back through this function).
  const evalMemo: EvalMemo = new Map();
  const parentChain = Array.from(visited);

  const flattenOneCell = (
    cellTransform: BlockInsertTransform,
    parentEntityId: string,
    // Round-6 finding 2 — the OUTER residual applied to a WORLD `overridePosition`
    // at THIS top level: IDENTITY for a plain / base-cell INSERT, a pure
    // translation by the grid offset for a non-base MINSERT cell (an imported
    // ATTRIB's group-10 is positioned for the base cell; each repeated cell
    // must shift the label by the same grid step the geometry moves).
    outer: AttribOuterTransform,
  ): RenderableEntity[] =>
    flattenTypedInsert({
      blockName: insert.blockName,
      transform: cellTransform,
      spaceId: insert.spaceId,
      parentEntityId,
      blocks,
      ctx: childCtx,
      // @codex #431 round-3 — the effective layer children on "0" inherit
      // (AutoCAD block-layer semantics). At the top level it is the INSERT's
      // own layer (there is no outer chain to defer to).
      parentLayer: insert.layer ?? "0",
      // S10c — the top-level INSERT's own ATTRIB data; threaded to Phase A
      // ATTDEF value substitution inside the per-child loop.
      attributes: insert.attributes ?? [],
      // S10e — this INSERT's OWN per-instance dynamic-block state (module
      // doc "Dynamic-block evaluation"). SAME for every MINSERT cell
      // (mirrors the pre-existing `attributes` comment above — AutoCAD has
      // no per-cell variation mechanism), which is exactly why sharing ONE
      // `evalMemo` across cells is correct, not just an optimisation.
      customProperties: insert.customProperties ?? {},
      visibilityState: insert.visibilityState,
      parentChain,
      evalMemo,
      outer,
      // The top-level INSERT's OWN layer is stamped by
      // `scene/liftBlockInsert.ts` (the single render call site), so the chain
      // recorded here holds only the NESTED references below it.
      ancestorLayers: EMPTY_ANCESTOR_LAYERS,
      ancestryOut,
      visited,
      depth,
    });

  // MINSERT grid (S10a — see this function's doc comment). `Math.round`
  // matches the PDF-plot converter's own clamp
  // (`insertConverter.ts insertToPrimitives`); `finiteOr` guards a
  // non-parsed test literal / malformed import the same way every other
  // field on this INSERT is guarded above.
  const columnCount = Math.max(1, Math.round(finiteOr(insert.columnCount, 1)));
  const rowCount = Math.max(1, Math.round(finiteOr(insert.rowCount, 1)));

  if (columnCount === 1 && rowCount === 1) {
    // Plain INSERT — the world `overridePosition` is already final (no grid,
    // no outer chain at the top level): IDENTITY residual.
    return flattenOneCell(transform, insert.id, IDENTITY_ATTRIB_OUTER);
  }

  // Round-2 FINDING 4 — bound the total number of full-flatten cells (doc
  // comment on `MAX_MINSERT_CELLS`). A schema-valid but extreme grid renders
  // only its first `MAX_MINSERT_CELLS` cells in row-major order (the SAME
  // order the loop below already walks) instead of hanging the render path.
  const totalCells = columnCount * rowCount;
  const cappedCells = Math.min(totalCells, MAX_MINSERT_CELLS);
  if (totalCells > MAX_MINSERT_CELLS && typeof console !== "undefined") {
    console.warn(
      `[scene3d] blockFromEntity: INSERT '${insert.id}' MINSERT grid ` +
        `${columnCount}×${rowCount} (${totalCells} cells) exceeds the ` +
        `${MAX_MINSERT_CELLS}-cell render cap — rendering only the first ` +
        `${MAX_MINSERT_CELLS} cells (row-major order).`,
    );
  }

  const columnSpacing = finiteOr(insert.columnSpacing, 0);
  const rowSpacing = finiteOr(insert.rowSpacing, 0);
  const out: RenderableEntity[] = [];
  let cellIndex = 0;
  for (let row = 0; row < rowCount && cellIndex < cappedCells; row++) {
    for (let col = 0; col < columnCount && cellIndex < cappedCells; col++) {
      cellIndex++;
      if (row === 0 && col === 0) {
        // Byte-identical parent id / transform for the origin cell — a
        // plain (non-MINSERT-grid) INSERT stays untouched. The imported
        // ATTRIB's group-10 already positions the BASE cell, so its outer
        // residual is IDENTITY (zero grid offset).
        out.push(...flattenOneCell(transform, insert.id, IDENTITY_ATTRIB_OUTER));
        continue;
      }
      // The per-cell offset rides the block's own rotate+scale (module
      // header) — `transformBlockDirection` is the pure linear map (no
      // translation / basePoint subtract), exactly what a MINSERT step
      // vector needs.
      const offset = transformBlockDirection(
        { x: col * columnSpacing, y: row * rowSpacing, z: 0 },
        transform,
      );
      const cellTransform: BlockInsertTransform = {
        insertion: {
          x: transform.insertion.x + offset.x,
          y: transform.insertion.y + offset.y,
          z: transform.insertion.z + offset.z,
        },
        scale: transform.scale,
        rotationDeg: transform.rotationDeg,
      };
      // Round-6 finding 2 — a WORLD `overridePosition` on this INSERT is placed
      // for the BASE cell; this repeated cell must shift the label by the SAME
      // grid offset the geometry moves (a PURE TRANSLATION — the offset already
      // contains the block's rotate+scale, so the residual carries no further
      // rotation/scale). The block-LOCAL (unset) regime is unaffected: it rides
      // `cellTransform`, which already folds the offset into its insertion.
      const cellOuter: AttribOuterTransform = {
        transform: {
          insertion: { x: offset.x, y: offset.y, z: offset.z },
          scale: { x: 1, y: 1, z: 1 },
          rotationDeg: 0,
        },
        basePoint: { x: 0, y: 0, z: 0 },
      };
      out.push(
        ...flattenOneCell(
          cellTransform,
          `${insert.id}::minsert-r${row}c${col}`,
          cellOuter,
        ),
      );
    }
  }
  return out;
}

/**
 * Resolve a TOP-LEVEL INSERT's annotative visibility + world transform — the
 * single truth for BOTH the render flatten ({@link blockFromEntity}) and the
 * cache-direct `block-insert` wrapper (`scene/liftBlockInsert.ts`, which
 * needs the DISPLAYED insertion / scale / rotation for its geometry payload
 * and OSNAP "insertion" point without re-deriving the per-scale override).
 *
 * Returns `null` when the INSERT is annotatively HIDDEN at the active scale
 * (the caller draws nothing); otherwise the normalised transform.
 *
 * S10d — the active-scale annotative representation is resolved first (single
 * truth: `resolveCannoscaleContext`). A non-annotative INSERT / no active scale
 * resolves to `isVisible: true`, `activeContext: null`, so the master fields
 * are used unchanged (golden parity intact).
 *
 * S10d round-19/21 (@codex, blockFromEntity:235/245) — the ONE-TRUTH INSERT
 * visibility gate (`insertAnnotativeHidden`): the per-scale resolver PLUS the
 * OBJECTSCALE `annotationScales` allow-list, which the reducer mutates
 * independently of `annotativeContexts`. An active scale that IS an explicit
 * allow-list member SHOWS (master-field fallback for a not-yet-seeded context —
 * round-21); a non-member (non-empty list) HIDES even over a stale matched
 * context (round-19/20). Golden parity (no active scale) resolves to visible.
 *
 * Top-level normalisation replicates the legacy `flattenBlockInsertEntity`
 * guards (finite / |s| > 1e-9 → else default). S10d — a matched blockref
 * representation re-places + re-scales + re-rotates the whole INSERT per scale
 * (insertion / scaleX / scaleY / rotation from the context, verbatim; NOT
 * multiplied by effectiveScale — AutoCAD writes the already annotation-scaled
 * X/Y into the context record). Each field still rides the SAME `finiteOr` /
 * `guardScale` clamp as the master path so a degenerate context value cannot
 * collapse the block.
 */
export function resolveBlockInsertTransform(
  insert: BlockInsertEntity,
  ctx: AdapterContext,
): BlockInsertTransform | null {
  const anno = resolveTypedAnnotation(insert.annotativeContexts, ctx);
  if (insertAnnotativeHidden(anno, insert.annotationScales, ctx)) return null;
  return {
    insertion: {
      x: finiteOr(anno.insertion?.x ?? insert.insertionPoint.x, 0),
      y: finiteOr(anno.insertion?.y ?? insert.insertionPoint.y, 0),
      z: finiteOr(insert.insertionPoint.z ?? 0, 0),
    },
    scale: {
      x: guardScale(anno.scaleX ?? insert.scaleX),
      y: guardScale(anno.scaleY ?? insert.scaleY),
      z: guardScale(insert.scaleZ),
    },
    rotationDeg: finiteOr(anno.rotation ?? insert.rotation, 0),
  };
}

/**
 * DEFUSE S11 — typed twin of the deleted legacy low-level
 * `flattenBlockInsert` args-entry: flatten a block REFERENCE given raw
 * transform components instead of a full `BlockInsertEntity`. For the
 * synthetic-reference callers that never had a real INSERT entity:
 * MLEADER block content, MText embedded blocks, and the instanced-INSERT
 * shared-geometry materializer.
 *
 * Parity contract with the legacy entry (which these callers used):
 *
 *   - `rotationDeg` is DEGREES (the legacy `rotation` argument's contract).
 *   - NO insert-level BYBLOCK colour / linetype / group-48 threading happens
 *     here — the legacy low-level entry received `ctx` verbatim too (that
 *     threading belongs to the entity entry `blockFromEntity`, twin of the
 *     deleted `flattenBlockInsertEntity`). Callers that need a thread pass it
 *     in `ctx` themselves.
 *   - `parentLayer` defaults to "0", so children keep their authored layers
 *     verbatim (`resolveBlockChildLayer(child, "0")` is the identity) —
 *     byte-parity with the legacy flatten, which never rewrote child layers.
 *   - offset-space equivalence: the legacy entry emitted
 *     `applyOffset(child) + insertion` while this one folds the insertion
 *     into the transform BEFORE the per-kind adapters offset once —
 *     `applyOffset(child + insertion)`. `applyOffset` is a pure translation,
 *     so for the already-offset insertions these callers pass the two
 *     compositions are IDENTICAL (unlike the top-level entity path, where the
 *     legacy double-`applyOffset` under a locked origin was a documented
 *     legacy-wrong carve-out).
 *   - ATTRIB data (S10c + round-? FINDING 3): these synthetic callers never
 *     had a real `BlockInsertEntity.attributes[]` to begin with (MLEADER
 *     block content, MText embedded blocks, and instanced-INSERT shared
 *     geometry all synthesize a bare transform, not a full INSERT record).
 *     An optional `attributes` param lets a caller that DOES have per-
 *     reference attribute data (MLEADER's `blockAttributeReferences[]`,
 *     mapped to the referenced ATTDEF's tag by
 *     `multileaderToPrimitives.ts` — mirrors the SVG twin
 *     `renderMultileader.tsx`'s `attrLookup`) thread it through to
 *     `attdefFromEntity`'s Phase A tag match. Callers that omit it (MText
 *     embedded blocks, instanced-INSERT shared geometry — neither carries
 *     attribute data) get the pre-existing behaviour: a variable ATTDEF
 *     inside the referenced block falls back to its own `defaultValue`
 *     (Phase A's "no matching ATTRIB" branch — never blank); a constant
 *     ATTDEF renders normally (Phase B, unaffected).
 *   - NO MINSERT grid (S10a): same synthetic-caller rationale — none of
 *     these callers had a real `columnCount`/`rowCount` to begin with.
 *   - Dynamic-block state (S10e): same synthetic-caller rationale as
 *     ATTRIB data above — none of these callers (MLEADER block content,
 *     MText embedded blocks, instanced-INSERT shared geometry) carry a
 *     per-reference `customProperties` / `visibilityState` in the typed
 *     model, so the referenced block evaluates at its AUTHORED DEFAULT
 *     state (`evaluateBlock`'s own default-overlay — `applyInstanceParameters`
 *     with an empty override bag). Strictly better than the pre-S10e
 *     "every visibility state superimposed"; a genuinely dynamic block used
 *     as MLEADER/MText content now renders its one default state instead.
 */
export function flattenBlockReference(
  args: BlockReferenceFlattenArgs,
): RenderableEntity[] {
  return flattenTypedInsert({
    blockName: args.blockName,
    transform: {
      insertion: {
        x: finiteOr(args.insertion.x, 0),
        y: finiteOr(args.insertion.y, 0),
        z: finiteOr(args.insertion.z, 0),
      },
      scale: {
        x: guardScale(args.scale.x),
        y: guardScale(args.scale.y),
        z: guardScale(args.scale.z),
      },
      rotationDeg: finiteOr(args.rotationDeg, 0),
      // The SVD inner rotation, when the caller composed an enclosing affine
      // onto a rotated reference (`blockReferenceFlatten.ts`
      // `preRotationDeg`); `undefined` keeps the plain triple.
      ...(args.preRotationDeg
        ? { preRotationDeg: finiteOr(args.preRotationDeg, 0) }
        : {}),
    },
    spaceId: args.spaceId,
    parentEntityId: args.parentEntityId,
    blocks: args.blocks,
    ctx: args.ctx,
    parentLayer: args.parentLayer ?? "0",
    // Round-? FINDING 3 — threaded from the caller when available (see the
    // new `attributes` doc comment above); [] preserves the pre-existing
    // behaviour for callers that omit it.
    attributes: args.attributes ?? [],
    // S10e — no per-reference dynamic state (doc comment "Dynamic-block
    // state" above); evaluates at the block's authored default.
    customProperties: {},
    visibilityState: undefined,
    parentChain: [],
    // S10e — fresh per call (no grid / no sibling recursion to dedupe
    // across for a single synthetic reference; module doc "Dynamic-block
    // evaluation").
    evalMemo: new Map(),
    // Round-6 findings 2+5 — a synthetic block reference has no MINSERT grid
    // and no enclosing block chain of its own, so a WORLD `overridePosition`
    // on any threaded attribute is already final: IDENTITY residual.
    outer: IDENTITY_ATTRIB_OUTER,
    // A synthetic reference (MLEADER block content / MTEXT embedded block) is
    // re-homed onto its OWNER's layer by the satellite collector
    // (`scene/renderSatellites.ts`), so it carries no nested-reference chain of
    // its own and needs no ancestry sink.
    ancestorLayers: EMPTY_ANCESTOR_LAYERS,
    ancestryOut: undefined,
    visited: new Set<string>(),
    depth: 0,
  });
}

/** Accumulated-recursion arguments (typed twin of `FlattenInsertArgs`). */
interface FlattenTypedInsertArgs {
  blockName: string;
  transform: BlockInsertTransform;
  spaceId: string;
  parentEntityId: string;
  blocks: Record<string, BlockDefinition>;
  ctx: AdapterContext;
  /** Effective layer of the enclosing INSERT chain — see
   *  `resolveBlockChildLayer` (@codex #431 round-3). */
  parentLayer: string;
  /**
   * S10c — the OWNING INSERT's `attributes[]` (top-level `blockFromEntity`:
   * `insert.attributes`; nested `Block` recursion: the child's OWN
   * `attributes[]`; `flattenBlockReference`'s synthetic callers: `[]`).
   * Consumed by `attdefFromEntity`'s Phase A ATTDEF↔ATTRIB tag match.
   */
  attributes: readonly BlockInsertAttribute[];
  /**
   * S10e — THIS level's OWNING INSERT's per-instance dynamic-block state
   * (top-level `blockFromEntity`: `insert.customProperties`; nested `Block`
   * recursion: the child's OWN `customProperties`; `flattenBlockReference`'s
   * synthetic callers: `{}`). Threaded to `evaluateBlock` — module doc
   * "Dynamic-block evaluation".
   */
  customProperties: Readonly<Record<string, string | number | boolean>>;
  /** S10e — companion to `customProperties`; same per-caller mapping. */
  visibilityState: string | undefined;
  /**
   * S10e — ancestor block-NAME chain (mirrors `visited`, WITHOUT this
   * level's own `blockName`) threaded to `evaluateBlock`'s own depth/cycle
   * guard. Also the memo-key prefix (module doc "Dynamic-block evaluation").
   */
  parentChain: readonly string[];
  /** S10e — per-top-level-call `evaluateBlock` memo, threaded unchanged
   *  through every recursion level. See {@link EvalMemo}. */
  evalMemo: EvalMemo;
  /**
   * Round-6 findings 2+5 — the accumulated OUTER (residual) transform applied
   * to a WORLD `overridePosition` on THIS level's attributes (top-level plain
   * INSERT / synthetic reference: IDENTITY; MINSERT non-base cell: a pure grid
   * translation; nested `Block` recursion: the PARENT level's `t`). See
   * `attdefFromEntity.ts` module header "The OUTER-transform decomposition".
   */
  outer: AttribOuterTransform;
  /**
   * Codex PR #784 round 3 P1 — the layers of the enclosing NESTED block
   * references, outermost first, EXCLUDING the top-level INSERT's own layer
   * (which `scene/liftBlockInsert.ts` prepends for both render branches).
   * `[]` at the top level and for every synthetic
   * {@link flattenBlockReference} caller; each nested `Block` recursion
   * appends ITS effective layer (`resolveBlockChildLayer`, so a nested INSERT
   * authored on "0" contributes the inherited layer, not "0"). See
   * {@link BlockChildAncestry}.
   */
  ancestorLayers: readonly string[];
  /** Codex PR #784 round 3 P1 — out-of-band ancestry sink; see
   *  {@link BlockChildAncestry}. `undefined` disables the recording. */
  ancestryOut: BlockChildAncestry | undefined;
  visited: ReadonlySet<string>;
  depth: number;
}

/**
 * Codex PR #784 round 3 P1 — per-child ancestry sink: renderable id → the
 * layers of the enclosing NESTED block references, outermost first.
 *
 * # Why OUT-OF-BAND
 *
 * AutoCAD hides a block reference as a WHOLE when ITS layer is off / frozen,
 * so a child inside `outer INSERT on A ⊃ nested INSERT on C` must hide when
 * EITHER A or C is off — the child's visibility chain is `[A, C]`, not `[A]`
 * (`../../layerChainVisibility.ts`). The chain is built HERE (the only place
 * that knows the nesting) but deliberately NOT written onto the emitted
 * renderables: this function's output is parity-pinned against the leaf
 * adapters and the frozen legacy oracle by whole-object `toEqual` assertions
 * (`__tests__/block-flatten-superiority.test.ts`,
 * `__tests__/block-flatten-codex-rounds.test.ts`,
 * `__tests__/block-flatten-parity.test.ts`), none of which know such a field.
 * `scene/liftBlockInsert.ts` — the SINGLE render call site — reads this map
 * and stamps `RenderableEntity.renderParentLayers = [insert.layer, ...chain]`
 * on its OWN shallow copies, so both render branches get the full chain while
 * the flatten's own objects stay byte-identical.
 *
 * Ids are unique within one flatten (`${parentEntityId}::${child.id}-${i}`,
 * and a nested level's `parentEntityId` embeds the nested INSERT's own id),
 * so a map keyed by id cannot collide across levels.
 */
export type BlockChildAncestry = Map<string, readonly string[]>;

/** Shared empty chain — the top level and every synthetic reference. */
const EMPTY_ANCESTOR_LAYERS: readonly string[] = [];

/**
 * Record `ancestorLayers` for every renderable a recursion level emitted that
 * does not already carry a DEEPER chain (a deeper level recorded its own
 * children before this level's `out.push(...nested)` merged them in).
 * No-op at the top level (`ancestorLayers` empty) and when no sink is
 * threaded, so the common non-nested flatten pays one length check.
 */
function recordChildAncestry(
  renderables: readonly RenderableEntity[],
  ancestorLayers: readonly string[],
  ancestryOut: BlockChildAncestry | undefined,
): void {
  if (ancestryOut === undefined || ancestorLayers.length === 0) return;
  for (const r of renderables) {
    if (!ancestryOut.has(r.id)) ancestryOut.set(r.id, ancestorLayers);
  }
}

function flattenTypedInsert(args: FlattenTypedInsertArgs): RenderableEntity[] {
  const {
    blockName,
    transform: t,
    spaceId,
    parentEntityId,
    blocks,
    ctx,
    parentLayer,
    attributes,
    customProperties,
    visibilityState,
    parentChain,
    evalMemo,
    outer,
    ancestorLayers,
    ancestryOut,
    visited,
    depth,
  } = args;

  // Cycle guard + depth cap — legacy semantics (`blockInsert.ts:661-681`):
  // a re-entered block name OR depth ≥ 50 emits the red placeholder square
  // + "RECURSION:<name>" label instead of descending.
  if (visited.has(blockName) || depth >= MAX_TYPED_BLOCK_RECURSION_DEPTH) {
    const placeholder = buildTypedRecursionPlaceholder(
      blockName,
      t.insertion,
      t.scale,
      spaceId,
      parentEntityId,
      ctx,
    );
    // The placeholder square / label is drawn INSIDE the enclosing reference
    // chain, so it hides with it exactly like real child geometry.
    recordChildAncestry(placeholder, ancestorLayers, ancestryOut);
    return placeholder;
  }

  // Codex PR #784 round 3 P2 — OWN-property-safe block-table read. `blocks` is
  // a plain `Record<string, BlockDefinition>` built from the DXF BLOCKS section,
  // and `blockName` is a raw DXF group-2 name a malformed / hand-edited file can
  // set to ANY string, `"constructor"` / `"toString"` / `"__proto__"` included. A
  // bare index then returns the INHERITED `Object.prototype` member instead of
  // `undefined`, so the `!blockDef` guard passes and `blockDef.basePoint` throws
  // — aborting the whole synchronous bulk load (or blanking the async pass) now
  // that the cache-direct INSERT lift routes through this flatten. Same
  // `safeRecordLookup` one-truth cad-core already uses for every block-table read.
  const blockDef = safeRecordLookup(blocks, blockName);
  if (!blockDef) {
    // Legacy parity (`blockInsert.ts:693-705`): warn + empty output.
    if (typeof console !== "undefined") {
      console.warn(`[scene3d] blockFromEntity: block '${blockName}' not found`);
    }
    return [];
  }

  const nextVisited = new Set(visited);
  nextVisited.add(blockName);

  const basePoint: Vec3 = {
    x: blockDef.basePoint.x,
    y: blockDef.basePoint.y,
    z: blockDef.basePoint.z,
  };

  // S10e — resolve this level's PER-INSTANCE entity list ONCE; Phase A and
  // Phase B below both walk THIS same list (module doc "Dynamic-block
  // evaluation" — required, not incidental, so a visibility-hidden ATTDEF
  // and its substituted ATTRIB value hide together).
  const evaluatedChildren = resolveEvaluatedChildren(
    blockDef,
    blocks,
    customProperties,
    visibilityState,
    parentChain,
    evalMemo,
  );

  const out: RenderableEntity[] = [];

  // ── Phase A — ATTRIB value resolution (S10c — LANDED, Lane F/D2) ──────
  // `attributes` (this recursion level's OWNING INSERT's `attributes[]`) →
  // match block ATTDEF BY TAG → render the resolved VALUE (or the constant
  // ATTDEF's own `defaultValue`) with ATTDEF-inherited / EATTEDIT-override
  // geometry, skipping invisible attributes — `attdefFromEntity` (module
  // doc: full Phase A/B contract).
  //
  // NB: MINSERT grid expansion (`insert.columnCount/rowCount/…`) does NOT
  // happen here — `blockFromEntity`'s doc comment explains why (top-level
  // only, matching `insertConverter.ts`'s PDF-plot precedent).

  // Round-? FINDING 4 / round-4 FINDING 4 — tracks, PER TAG, how many VARIABLE
  // ATTDEF occurrences exist (0 for a tag never seen, N for N same-tag
  // VARIABLE ATTDEFs). Round-5 FINDING 3 — CONSTANT ATTDEFs are excluded
  // (they carry no per-instance ATTRIB row, so counting them would shift a
  // later variable ATTDEF's occurrence index past its real matching row).
  // S10e — built from the FULL, UNFILTERED `blockDef.entities` (NOT
  // `evaluatedChildren` — module doc "Dynamic-block evaluation"): `attributes[]`
  // row↔ATTDEF occurrence matching is fixed at INSERT-attach time against the
  // AUTHORED ATTDEF list, independent of the ACTIVE instance's visibility
  // state — counting from the visibility-filtered list instead would
  // misalign the index whenever a PRECEDING same-tag ATTDEF is hidden in the
  // active state. `staticOccurrenceByChildId` records each VARIABLE ATTDEF's
  // occurrence index by id so the Phase-B render loop below (which walks
  // `evaluatedChildren`, a subsequence of this same static list) can look it
  // up directly instead of re-counting over a filtered view. For a STATIC
  // block `evaluatedChildren === blockDef.entities` by reference, so this
  // precompute reproduces the pre-S10e inline-counted result byte-for-byte.
  // Three jobs:
  //   1. Selects the matching-by-POSITION ATTRIB row for a duplicate-tag
  //      ATTDEF (`attdefFromEntity`'s `occurrenceIndex` param) instead of
  //      always the first — a block-definition duplicate tag (authoring does
  //      not reject it) previously made every duplicated placeholder render
  //      the SAME (first) row's value.
  //   2. Lets the orphan pass (after the loop) find `attributes[]` entries
  //      with NO matching ATTDEF occurrence at all — the ATTDEF was removed/
  //      renamed after this INSERT was placed/EATTEDIT'd (the model preserves
  //      the row, it is never pruned) OR a same-tag ATTRIB row exists beyond
  //      the last ATTDEF occurrence for that tag (a malformed/hand-edited
  //      duplicate — rendered via the orphan fallback rather than silently
  //      dropped, matching CLAUDE.md "never simplify away data").
  //   3. (S10e) Correctly treats a visibility-state-HIDDEN ATTDEF as
  //      "consumed" (NOT orphaned) even though Phase B never renders it —
  //      matching AutoCAD, where a hidden ATTDEF's attribute value
  //      disappears with it instead of falling back to a standalone render.
  const attdefTagOccurrenceCount = new Map<string, number>();

  // Round-5 FINDING 3 — tags carried by at least one CONSTANT ATTDEF. A
  // constant's value is authoritative (it has no per-instance ATTRIB), so any
  // stray same-tag `attributes[]` row is DISCARDED, not resurrected by the
  // orphan pass. Because constants no longer bump `attdefTagOccurrenceCount`
  // (so they cannot steal a variable ATTDEF's row), the orphan pass would
  // otherwise treat such a stray as unconsumed and render it standalone —
  // regressing the "constant wins, stray ignored" contract. This set lets the
  // orphan pass swallow those rows. S10e — also built from the static list,
  // same rationale as `attdefTagOccurrenceCount` above.
  const constantAttdefTags = new Set<string>();

  const staticOccurrenceByChildId = new Map<string, number>();
  for (const staticChild of blockDef.entities) {
    if (staticChild.type !== "Attdef") continue;
    if (staticChild.flags.constant) {
      constantAttdefTags.add(staticChild.tag);
      continue; // constants never occupy an attributes[] row
    }
    const occurrenceIndex = attdefTagOccurrenceCount.get(staticChild.tag) ?? 0;
    attdefTagOccurrenceCount.set(staticChild.tag, occurrenceIndex + 1);
    staticOccurrenceByChildId.set(staticChild.id, occurrenceIndex);
  }

  // The block-content flattener every COMPOSITE child arm below injects (the
  // block-child MULTILEADER's `contentType === 1` content and, @codex PR #785
  // round 1 F1, the block-child ACAD_TABLE's `*T<N>` graphics block).
  //
  // It is a closure over THIS level's recursion state (`nextVisited` /
  // `depth + 1`) rather than the module-level `flattenBlockReference` (which
  // resets both): a composite child whose content references an ENCLOSING
  // block would otherwise re-enter this block name with a fresh guard and
  // recurse forever. Everything else matches `flattenBlockReference`'s
  // synthetic-reference contract (fresh eval memo, identity ATTRIB outer, no
  // ancestry sink — the content is re-homed onto the owning child's own layer
  // by `scene/renderSatellites.ts`). Hoisted out of the MLEADER arm so both
  // composite arms share ONE definition instead of two copies that could
  // drift.
  const flattenBlockContent: BlockContentFlattener = (contentArgs) =>
    flattenTypedInsert({
      blockName: contentArgs.blockName,
      transform: {
        insertion: {
          x: finiteOr(contentArgs.insertion.x, 0),
          y: finiteOr(contentArgs.insertion.y, 0),
          z: finiteOr(contentArgs.insertion.z, 0),
        },
        scale: {
          x: guardScale(contentArgs.scale.x),
          y: guardScale(contentArgs.scale.y),
          z: guardScale(contentArgs.scale.z),
        },
        rotationDeg: finiteOr(contentArgs.rotationDeg, 0),
        // Codex round-6 P2 — the enclosing INSERT affine composed onto the
        // MLEADER's own content reference is `R·S·R` (a non-uniform scale
        // around a rotated reference SHEARS, so it cannot fold into one
        // rotation). Threaded verbatim; absent for an unrotated content
        // reference, which keeps the pre-existing triple.
        ...(contentArgs.preRotationDeg
          ? { preRotationDeg: finiteOr(contentArgs.preRotationDeg, 0) }
          : {}),
      },
      spaceId: contentArgs.spaceId,
      parentEntityId: contentArgs.parentEntityId,
      blocks: contentArgs.blocks,
      ctx: contentArgs.ctx,
      parentLayer: contentArgs.parentLayer ?? "0",
      attributes: contentArgs.attributes ?? [],
      customProperties: {},
      visibilityState: undefined,
      parentChain: [...parentChain, blockName],
      evalMemo: new Map(),
      outer: IDENTITY_ATTRIB_OUTER,
      ancestorLayers: EMPTY_ANCESTOR_LAYERS,
      ancestryOut: undefined,
      visited: nextVisited,
      depth: depth + 1,
    });

  // ── Phase B — generic dispatch with explicit carve-outs ──────────────
  // S10e — walks `evaluatedChildren` (this level's PER-INSTANCE resolved
  // list), NOT `blockDef.entities` (module doc "Dynamic-block evaluation").
  for (let i = 0; i < evaluatedChildren.length; i++) {
    const child = evaluatedChildren[i]!;

    // S10d (round-18 FINDING, typedFirstRenderable:127 block path) — a block-
    // nested annotative TEXT / MTEXT / HATCH with NO representation at the
    // active CANNOSCALE is HIDDEN when ANNOALLVISIBLE=false. The top-level
    // gate (`renderableFromDrawingEntity`) never runs for block children (they
    // dispatch through `entityToRenderableTyped` / `transformBlockChild`
    // directly), so apply the SAME visibility decision here. Non-annotative
    // children / no active scale resolve to `isVisible: true` (golden parity).
    // The matched-context per-scale GEOMETRY override is carried into world
    // space by `transformBlockChild` (`transformChildAnnotativeContexts`) and
    // applied by the leaf adapter; only the hide decision needs `ctx` here.
    if (
      (child.type === "Text" ||
        child.type === "MText" ||
        child.type === "Hatch") &&
      !resolveTypedAnnotation(child.annotativeContexts, ctx).isVisible
    ) {
      continue;
    }

    // Carve-out 2 — nested INSERT recurses with the accumulated transform
    // (legacy `blockInsert.ts:734-796`): triple-accumulation `scale·child`,
    // `rotation+child`, transformed child insertion; the dispatcher would
    // return null for `Block` and silently drop the subtree.
    if (child.type === "Block") {
      // S10d (round-18 FINDING, blockFromEntity:236) — a nested annotative
      // INSERT resolves its OWN per-scale representation, exactly like the
      // top-level entry above: hidden at the active scale (ANNOALLVISIBLE=false,
      // no matching representation) → skip the WHOLE subtree; a matched
      // blockref context re-places / re-scales / re-rotates the nested INSERT.
      // Its context insertion / scaleX / scaleY / rotation live in THIS
      // (enclosing) block's LOCAL frame — the same frame as `child.insertionPoint`
      // — so the resolved insertion still rides `transformBlockChildPoint(_,
      // basePoint, t)` into world, and the scale/rotation compose onto `t`
      // identically to the master fields. Non-annotative nested INSERT / no
      // active scale → `isVisible: true`, no override (golden parity intact).
      const childAnno = resolveTypedAnnotation(child.annotativeContexts, ctx);
      // S10d round-19/21 (@codex, blockFromEntity:235/245) — a nested annotative
      // INSERT honours its OWN `annotationScales` allow-list too, through the SAME
      // one-truth gate as the top-level entry (the OBJECTSCALE reducer can seed the
      // list without a per-scale context on a nested reference as well; an active
      // member scale SHOWS with master fields, a non-member HIDES).
      if (insertAnnotativeHidden(childAnno, child.annotationScales, ctx)) continue;
      const childInsertion = transformBlockChildPoint(
        {
          x: childAnno.insertion?.x ?? child.insertionPoint.x,
          y: childAnno.insertion?.y ?? child.insertionPoint.y,
          // The legacy recursion hardcodes the nested insertion z to
          // `-basePoint.z` (drops the child's own z); typed reads the model
          // field — same bag-starvation closure as the top-level entry. A
          // blockref context carries no z (2D `annotScalePoint`), so the
          // master z survives a matched context.
          z: child.insertionPoint.z ?? 0,
        },
        basePoint,
        t,
      );
      // @codex #431 round-3 — the nested INSERT's EFFECTIVE layer: a nested
      // INSERT authored on "0" defers to the enclosing chain (innermost
      // non-"0" wins — `insertConverter.ts:426-436` PDF-6.2a propagation).
      // Its own ByLayer colour / linetype resolve against this effective
      // layer, and ITS children inherit it.
      const nestedLayer = resolveBlockChildLayer(
        child.layer ?? "0",
        parentLayer,
      );
      // @codex #431 round-4 — the nested INSERT's OWN scale components get
      // the SAME degenerate guard the top-level entry applies (finite /
      // |s| > 1e-9 → else 1): `blockInsertEntitySchema` permits 0, which
      // would collapse the subtree to a point and blow the |sx|/|sy|
      // width-factor ratio to Infinity. The legacy flatten multiplies the raw
      // components (`blockInsert.ts:746-749` — a zero-scale nested INSERT
      // collapses there) — a legacy-wrong carve-out consistent with
      // normalising the SAME insert at top level; no golden fixture authors
      // degenerate nested scales.
      //
      // S10d — a matched blockref context's per-scale scaleX/scaleY/rotation
      // replace the master fields (AutoCAD writes the already-scaled
      // components into the context record), still guarded + composed onto
      // `t` exactly like the master path.
      // The nested reference's own rotation + scale compose onto THIS level's
      // linear part through cad-core's ONE accumulation truth
      // (`composeNestedBlockLinear`): the pre-existing S10 triple fold when no
      // inner rotation is in force (byte-identical, golden-parity-pinned), and
      // the EXACT `R·S·R` composition when one is (MLEADER block content).
      // Round 7 P2: the exact arm matters because a nested NON-UNIFORM scale
      // must stay on the RIGHT of the inner rotation — folding it into the
      // accumulated `scale` drew Codex's `(1,0)` at `(4.24, 1.41)` instead of
      // `(4.24, 2.83)`. Dropping the rotation entirely would place a nested
      // INSERT inside MLEADER block content as if the content reference were
      // unrotated.
      const nestedLinear = composeNestedBlockLinear(blockLinearOfTransform(t), {
        rotationDeg: childAnno.rotation ?? child.rotation,
        scaleX: guardScale(childAnno.scaleX ?? child.scaleX),
        scaleY: guardScale(childAnno.scaleY ?? child.scaleY),
      });
      const nested = flattenTypedInsert({
        blockName: child.blockName,
        transform: {
          insertion: childInsertion,
          scale: {
            x: nestedLinear.scaleX,
            y: nestedLinear.scaleY,
            z: t.scale.z * guardScale(child.scaleZ ?? 1),
          },
          rotationDeg: nestedLinear.rotationDeg,
          // An inner rotation is threaded only when there IS one: absent keeps
          // the pre-existing triple byte-identical (`BlockInsertTransform`'s
          // `preRotationDeg` doc — every helper branches on absence).
          ...(nestedLinear.preRotationDeg
            ? { preRotationDeg: nestedLinear.preRotationDeg }
            : {}),
        },
        spaceId,
        parentEntityId: `${parentEntityId}::${child.id}`,
        blocks,
        // @codex #431 round-2 / FIX-2 / PR-LT3 — the nested INSERT's OWN
        // resolved colour + linetype + group-48 thread for ITS BYBLOCK
        // children (chain: a ByBlock nested INSERT inherits THIS block's
        // `ctx.blockColor`; `blockInsert.ts:755-776`), through the SAME
        // helper the top level uses. ByLayer resolves against the EFFECTIVE
        // layer (round-3 — a nested INSERT on "0" sits on the inherited one),
        // hence the explicit `layer: nestedLayer`.
        ctx: blockReferenceChildContext(
          {
            color: child.color,
            layer: nestedLayer,
            linetype: child.linetype,
            linetypeScale: child.linetypeScale,
            // The nested INSERT is drawn wherever the ENCLOSING reference is,
            // so it inherits that space (the flatten's `spaceId`) — its ACI 7
            // must contrast with the same background its parent's does.
            spaceId,
          },
          ctx,
        ),
        parentLayer: nestedLayer,
        // S10c — a nested INSERT may itself carry ATTRIBs (its OWN
        // `attributes[]`, NOT the enclosing chain's) — matches
        // `insertConverter.ts`'s "Nested INSERT may itself carry ATTRIBs".
        attributes: child.attributes ?? [],
        // S10e — the nested INSERT's OWN per-instance dynamic-block state
        // (module doc "Dynamic-block evaluation"; matches
        // `blockInstanceResolve.ts` `resolveNestedBlockChild`'s exact
        // convention for the SAME `child.customProperties` /
        // `child.visibilityState` fields — a nested `Block` child is a full
        // `blockInsertEntitySchema` member of `blockChildEntitySchema`).
        customProperties: child.customProperties ?? {},
        visibilityState: child.visibilityState,
        // `parentChain` for the NESTED level = this level's chain PLUS this
        // level's OWN block name (mirrors `nextVisited` = `visited +
        // blockName`, computed above).
        parentChain: [...parentChain, blockName],
        // Codex PR #784 round 3 P1 — the nested reference joins the layer
        // ancestry, in order, under its EFFECTIVE layer (`nestedLayer`: a
        // nested INSERT authored on "0" contributes the layer it actually
        // draws on). Freezing C then hides EVERYTHING inside `A ⊃ C`, not just
        // C's own layer-"0" children. A layer already in the chain adds
        // nothing to the conjunction, so it is not repeated (the chain of a
        // deeply layer-"0" nesting stays length 0).
        ancestorLayers: ancestorLayers.includes(nestedLayer)
          ? ancestorLayers
          : [...ancestorLayers, nestedLayer],
        ancestryOut,
        // Same memo instance threaded through every recursion level within
        // this ONE top-level `blockFromEntity` / `flattenBlockReference`
        // call (module doc "Dynamic-block evaluation").
        evalMemo,
        // Round-6 finding 5 — a nested INSERT's ATTRIB group-10 lives in THIS
        // (enclosing) block's local frame, not the drawing WCS. Its OUTER
        // residual is exactly THIS level's `t` (with THIS block's `basePoint`)
        // — the transform that maps this enclosing frame → world. `t` already
        // folds every level above (incl. any MINSERT cell offset from the top),
        // so the composition is correct to any nesting depth. The nested
        // INSERT's OWN affine (`child` translate/rotate/scale) is deliberately
        // EXCLUDED from the residual — the group-10 already bakes it in.
        outer: { transform: t, basePoint },
        visited: nextVisited,
        depth: depth + 1,
      });
      out.push(...nested);
      continue;
    }

    // Carve-out 1 — ATTDEF children never render standalone via the generic
    // dispatcher (a non-constant ATTDEF's OWN tag/defaultValue is never
    // shown at INSERT time — AutoCAD renders the ATTRIB value instead).
    // `attdefFromEntity` owns BOTH S10c arms: Phase A substitutes the
    // matching `attributes[]` entry's value for a variable ATTDEF, Phase B
    // renders the ATTDEF's own `defaultValue` for a constant one (module
    // doc: full contract, incl. the multiline fidelity boundary).
    if (child.type === "Attdef") {
      // Round-5 FINDING 3 — a CONSTANT ATTDEF has NO per-instance ATTRIB row
      // (its value is baked into the block definition — `createBlockInsertEntityFull`
      // / `add_auto_attribs` emit ATTRIBs only for VARIABLE ATTDEFs). A constant
      // renders its own `defaultValue` regardless of `occurrenceIndex`
      // (`resolveAttributeValue` ignores the ATTRIB for a constant), so pass 0.
      // S10e — the occurrence index for a VARIABLE ATTDEF is looked up from
      // `staticOccurrenceByChildId` (precomputed above, against the FULL
      // unfiltered `blockDef.entities` — see that precompute's doc comment
      // for why re-counting over `evaluatedChildren` here would be wrong).
      // `?? 0` covers an Array-action-duplicated ATTDEF copy (a synthetic
      // `${id}#arrN` extra absent from the static-id map) — an edge case far
      // outside normal ATTDEF authoring; falling back to row 0 is a safe,
      // non-crashing degrade, not a silent drop.
      const occurrenceIndex = child.flags.constant
        ? 0
        : (staticOccurrenceByChildId.get(child.id) ?? 0);
      const attdefRenderables = attdefFromEntity(
        child,
        attributes,
        occurrenceIndex,
        basePoint,
        t,
        {
          id: `${parentEntityId}::${child.id}-${i}`,
          spaceId,
          parentLayer,
        },
        ctx,
        outer,
      );
      out.push(...attdefRenderables);
      continue;
    }

    // @codex #431 P2 rounds 1+5 — a bulged LWPOLYLINE child explodes under
    // EVERY insert scale. The batcher now tessellates bulges (@codex PR #785
    // round 3 F1, `polylineCentrelineSamples`), but a bulge encodes a CIRCULAR arc
    // and the affine image of one under a non-uniform insert scale is an
    // ELLIPSE, so the curves still become true Arc/Ellipse renderables.
    // LOCAL-space explode into straight runs + per-bulge arc pieces, then
    // each piece rides the normal per-kind arms — Arc pieces where the
    // circle transform is exact (uniform non-mirrored), Ellipse pieces
    // through the exact conjugate-diameter map otherwise. See
    // `blockTransform.ts` module header "Bulged polylines"; bulge-free
    // polylines keep the single-renderable fast path.
    if (child.type === "LWPOLYLINE" && lwpolylineNeedsBulgeExplode(child)) {
      const pieces = explodeBulgedLwpolylineChild(
        child,
        `${parentEntityId}::${child.id}-${i}`,
        // Exact circle transform applies only when |sx| == |sy| WITHOUT a
        // mirror (the Arc arm maps angles by +rot only).
        Math.abs(t.scale.x) === Math.abs(t.scale.y) &&
          !isMirroredTransform(t),
      );
      for (const piece of pieces) {
        const transformedPiece = transformBlockChild(piece, basePoint, t, {
          id: piece.id,
          spaceId,
          parentLayer,
        });
        if (!transformedPiece) continue;
        const renderable = entityToRenderableTyped(transformedPiece, ctx);
        // The straight RUNS keep their per-vertex widths, so they need the
        // band frame exactly like an unexploded wide polyline does (@codex
        // PR #785 round 6, P2) — and they are exactly where a non-similarity
        // insert sends the explode in the first place.
        if (renderable)
          out.push(applyBlockChildBandFrame(renderable, piece, t));
      }
      continue;
    }

    // @codex round 17, FINDING B — the heavy 2D POLYLINE takes the SAME
    // decomposition, but only under a NON-SIMILARITY insert. A bulge encodes a
    // CIRCULAR arc and the affine image of one under unequal X/Y scales is an
    // ELLIPSE, which the clone re-drew as another circle through the mapped
    // endpoints; under a SIMILARITY the authored bulge IS exact, and exploding
    // there would trade it for the band, the group-39 sweep and the group-50
    // tangents the Arc/Ellipse pieces cannot carry (`./blockBulgeExplode.ts`
    // argues both gates). Straight runs come back as heavy POLYLINE pieces, so
    // they still ride the band frame and the thickness stamp.
    if (child.type === "Polyline" && heavyPolylineNeedsBulgeExplode(child, t)) {
      const pieces = explodeBulgedHeavyPolylineChild(
        child,
        `${parentEntityId}::${child.id}-${i}`,
      );
      for (const piece of pieces) {
        const transformedPiece = transformBlockChild(piece, basePoint, t, {
          id: piece.id,
          spaceId,
          parentLayer,
        });
        if (!transformedPiece) continue;
        const renderable = entityToRenderableTyped(transformedPiece, ctx);
        if (renderable)
          out.push(
            applyBlockChildThicknessSweep(
              applyBlockChildBandFrame(renderable, piece, t),
              piece,
              t,
            ),
          );
      }
      continue;
    }

    // A block-child DIMENSION is generated in LOCAL space and rigidly
    // transformed primitive-by-primitive (NOT point-transformed then
    // re-derived) so the block SCALE/ROTATION carry into the DIMSTYLE-driven
    // arrow/extension/gap/text scalars and the horizontal-only linear emitter,
    // and the local `0`-sentinel / absent-extension fallbacks survive a
    // translation — @codex #510 ×4. `transformBlockChild` returns null for
    // Dimension (the wrong point-transform approach lived there).
    if (child.type === "Dimension") {
      const dimRenderable = renderBlockChildDimension(child, basePoint, t, ctx, {
        id: `${parentEntityId}::${child.id}-${i}`,
        spaceId,
        parentLayer,
      });
      if (dimRenderable) out.push(dimRenderable);
      continue;
    }

    // A block-child MULTILEADER (Codex PR #784 round 4 F1). Like DIMENSION
    // above it is NOT a `transformBlockChild` leaf: the callout's geometry is
    // DERIVED (leader walk, dogleg/landing, arrowheads, MTEXT / block content)
    // from scalar style fields the point transform cannot carry, and — for
    // `contentType === 1` — it re-enters THIS flatten for its own block
    // content. Before this arm both `transformBlockChild` and
    // `entityToRenderableTyped` returned `null` for `Multileader`, so an
    // INSERT whose definition holds an MLEADER dropped the ENTIRE callout
    // (leader lines, arrows, text AND its block content) on both render
    // branches.
    //
    // `flattenBlockContent` (hoisted above this loop) is the recursion-aware
    // closure the callout's own block content re-enters.
    if (child.type === "Multileader") {
      const mleaderRenderable = renderBlockChildMultileader({
        child,
        basePoint,
        t,
        ctx,
        blocks,
        identity: {
          id: `${parentEntityId}::${child.id}-${i}`,
          spaceId,
          parentLayer,
        },
        flattenBlockContent,
      });
      if (mleaderRenderable) out.push(mleaderRenderable);
      continue;
    }

    // A block-child ACAD_TABLE (@codex PR #785 round 1, F1). Like DIMENSION and
    // MULTILEADER above it is NOT a `transformBlockChild` leaf: a table IS a
    // block REFERENCE to the anonymous `*T<N>` block holding its rendered
    // graphics, and `AcDbTable` carries no scale factors, so the enclosing
    // INSERT affine has to be COMPOSED onto that reference (a clone could not
    // carry the scale at all). Before this arm both `transformBlockChild` and
    // `entityToRenderableTyped` returned `null` for `Table`, so an INSERT whose
    // definition holds a table dropped the whole table — every cell and rule.
    if (child.type === "Table") {
      const tableRenderable = renderBlockChildTable({
        child,
        basePoint,
        t,
        ctx,
        blocks,
        identity: {
          id: `${parentEntityId}::${child.id}-${i}`,
          spaceId,
          parentLayer,
        },
        flattenBlockContent,
      });
      if (tableRenderable) out.push(tableRenderable);
      continue;
    }

    // A block-child OLE2FRAME (@codex PR #785 round 2, F1). Not a
    // `transformBlockChild` leaf either: the entity stores only TWO opposite
    // corners and no rotation, so a CLONE can only ever describe an
    // axis-aligned box — under an oblique INSERT rotation the true frame is a
    // ROTATED rectangle, which the clone replaced with the larger upright box
    // around it. The arm maps the four corners and draws THAT quad, without
    // writing anything back to the persisted entity.
    if (child.type === "OLE Frame") {
      const oleRenderable = renderBlockChildOleFrame({
        child,
        basePoint,
        t,
        ctx,
        blocks,
        identity: {
          id: `${parentEntityId}::${child.id}-${i}`,
          spaceId,
          parentLayer,
        },
      });
      if (oleRenderable) out.push(oleRenderable);
      continue;
    }

    const transformed = transformBlockChild(child, basePoint, t, {
      id: `${parentEntityId}::${child.id}-${i}`,
      spaceId,
      parentLayer,
    });
    if (!transformed) continue;
    const renderable = entityToRenderableTyped(transformed, ctx);
    // A wide polyline child under a NON-SIMILARITY insert carries its AUTHORED
    // width (a width is not a scalar under such a map), so the renderable is
    // stamped with the frame its band must be built in and mapped through —
    // the order the vector plot has always used. Every other child, and every
    // similarity insert, is returned untouched (@codex PR #785 round 6, P2 —
    // `./blockChildBandFrame.ts`).
    //
    // A heavy 2D POLYLINE child's group-39 THICKNESS is the same shape of
    // problem: the image of its sweep is `M·(t·N̂)`, a VECTOR the clone's
    // scalar-times-group-210 cannot spell under a non-similarity map, so the
    // resolved world vector is stamped onto `SceneGeometry.thicknessSweep`
    // instead (@codex round 17, FINDING A — `./blockChildThicknessSweep.ts`).
    if (renderable)
      out.push(
        applyBlockChildThicknessSweep(
          applyBlockChildBandFrame(renderable, child, t),
          child,
          t,
        ),
      );
  }

  // Round-? FINDING 4 — orphan ATTRIB pass. The occurrence precompute above
  // only ever visits `blockDef.entities` (the ATTDEF side, S10e — the FULL
  // static list, not the visibility-filtered `evaluatedChildren` Phase B
  // renders), so an `attributes[]` entry whose tag matches NO ATTDEF in this
  // block definition is otherwise never rendered at all, even though the
  // document still carries it (BATTMAN removed/renamed the ATTDEF after
  // this INSERT was placed/EATTEDIT'd — the model never prunes the now-
  // orphaned row). `orphanAttributeFromEntity` mirrors the PDF-plot twin's
  // own always-attribute-driven fallback (module doc on that function).
  //
  // Round-4 FINDING 4 — a row is "consumed" (matched to an ATTDEF occurrence
  // above) only while its OWN within-tag occurrence index (this loop's
  // `attributeOccurrenceSeenByTag`, in `attributes[]` array order) is still
  // below that tag's TOTAL ATTDEF occurrence count
  // (`attdefTagOccurrenceCount`). For the common unique-tag case this is
  // exactly the previous `attdefTagsSeen.has(tag)` boolean (0 or 1 ATTDEF
  // occurrence). For a duplicate-tag block, the Nth row is consumed by the
  // Nth ATTDEF occurrence; any row BEYOND the last ATTDEF occurrence for its
  // tag renders as a genuine orphan instead of being silently dropped.
  const attributeOccurrenceSeenByTag = new Map<string, number>();
  for (let i = 0; i < attributes.length; i++) {
    const attr = attributes[i]!;
    const occurrenceIndex = attributeOccurrenceSeenByTag.get(attr.tag) ?? 0;
    attributeOccurrenceSeenByTag.set(attr.tag, occurrenceIndex + 1);
    const consumedCount = attdefTagOccurrenceCount.get(attr.tag) ?? 0;
    if (occurrenceIndex < consumedCount) continue;
    // Round-5 FINDING 3 — a same-tag row NOT matched to a variable ATTDEF but
    // whose tag belongs to a CONSTANT ATTDEF is a stray the constant
    // authoritatively swallows (not a genuine orphan) — discard it silently.
    if (constantAttdefTags.has(attr.tag)) continue;
    const orphanRenderables = orphanAttributeFromEntity(
      attr,
      basePoint,
      t,
      {
        id: `${parentEntityId}::orphan-attr-${i}`,
        spaceId,
        parentLayer,
      },
      ctx,
      outer,
    );
    out.push(...orphanRenderables);
  }

  recordChildAncestry(out, ancestorLayers, ancestryOut);
  return out;
}

/* ── helpers ─────────────────────────────────────────────────────────── */

/**
 * Render a block-child DIMENSION the AutoCAD way: generate the dimension
 * geometry in LOCAL (block) space via `dimensionFromEntity` — untransformed
 * definition points, style-driven DIMASZ/DIMEXO/DIMEXE/DIMGAP/DIMTXT — then
 * rigidly transform each emitted primitive by the block affine
 * ({@link transformDimensionPrimitive}). This is the "*D anonymous-block bake,
 * then transform the INSERT" model: the prior point-transform + world re-derive
 * (`transformBlockChild`) could NOT carry the block SCALE into the DIMSTYLE
 * scalars nor the block ROTATION into the horizontal-only linear emitter, and
 * destroyed the local `0`-sentinel defPoint / absent-extension fallbacks
 * (@codex #510 ×4).
 *
 * The LOCAL renderable's shell (resolved layer, ByLayer colour, lineweight,
 * zOrder) is reused verbatim — only the geometry primitives + `bounds` are
 * replaced with their block-transformed images. `bounds` is the AABB of the
 * transformed LOCAL bbox corners (contains the transformed geometry; loose-but-
 * safe under rotation, like every other block-child bbox).
 */
function renderBlockChildDimension(
  child: DimensionEntity,
  basePoint: Vec3,
  t: BlockInsertTransform,
  ctx: AdapterContext,
  identity: { id: string; spaceId: string; parentLayer: string },
): RenderableEntity | null {
  const localChild: DimensionEntity = {
    ...child,
    id: identity.id,
    spaceId: identity.spaceId,
    layer: resolveBlockChildLayer(child.layer, identity.parentLayer),
  };
  const local = dimensionFromEntity(localChild, ctx);
  if (local.geometry.kind !== "dimension") return local;
  const primitives = local.geometry.primitives.map((p) =>
    transformDimensionPrimitive(p, basePoint, t),
  );
  return {
    ...local,
    // The AABB of the transformed LOCAL bbox corners — ONE truth shared with
    // the MULTILEADER bake-then-place arm (`transformEmittedBounds`).
    bounds: transformEmittedBounds(local.bounds, basePoint, t),
    geometry: { ...local.geometry, primitives },
  };
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

/** Legacy scale guard (`flattenBlockInsertEntity:1080-1084`): a degenerate
 *  (non-finite / ~zero) component falls back to 1. ONE truth with the bounds
 *  walk's accumulated scale — `cad-core`'s `guardBlockScale`. */
const guardScale = guardBlockScale;

/**
 * Typed twin of the legacy `buildRecursionPlaceholder`
 * (`blockInsert.ts:546-611`): a unit square (scaled by the |scale| magnitude,
 * min 1) in ACI red on layer "0" + a `RECURSION:<block>` text label —
 * geometry replicated value-for-value so the S10 golden-parity harness can
 * deep-equal the legacy placeholder output. Built as typed entities
 * (`lineEntitySchema` / `cad-core`'s `recursionPlaceholderLabelEntity`, both
 * schema-parsed so the defaults are filled) and routed through the SAME typed
 * adapters as real children.
 */
function buildTypedRecursionPlaceholder(
  blockName: string,
  insertion: Vec3,
  scale: Vec3,
  spaceId: string,
  parentEntityId: string,
  ctx: AdapterContext,
): RenderableEntity[] {
  // ONE truth with the bounds walk's reserved footprint (`cad-core`'s
  // `recursionPlaceholder.ts` — Codex PR #784 round 7 P2): the square's size,
  // its corner order, the label's height and its contents all come from there,
  // so the drawn marker and the box `blockDefinitionWalk` reserves for it can
  // never drift. The emitted NUMBERS are unchanged (legacy parity).
  const sz = recursionPlaceholderSquareSize(scale.x, scale.y);
  const baseId = `${parentEntityId}::recursion`;
  // @codex #431 round-6 (P3) — the placeholder rides the transformed
  // `insertion.z` like every real child (Z family, `optionalPointZ`
  // pattern): the LINE endpoints carry z through the optional-z schema, and
  // the label's 2D `insertionPoint` gets it re-attached AFTER the parse
  // (the strict `drawingPointSchema` strips it; `textFromEntity` reads it
  // via `optionalPointZ`). An elevated cycle/depth warning now sits on the
  // plane of its lifted siblings instead of dropping to z=0. Golden
  // fixtures use z=0 (legacy's bag-starved insertion z) — parity intact.
  const corners = recursionPlaceholderSquareCorners(insertion, sz).map((c) => ({
    x: c.x,
    y: c.y,
    z: insertion.z,
  }));
  const primitives: RenderableEntity[] = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % 4]!;
    const line = lineEntitySchema.parse({
      type: "Line",
      id: `${baseId}-line-${i}`,
      spaceId,
      layer: "0",
      color: "1", // ACI red — visible warning (legacy parity).
      start: { x: a.x, y: a.y, z: a.z },
      end: { x: b.x, y: b.y, z: b.z },
    });
    const r = entityToRenderableTyped(line, ctx);
    if (r) primitives.push(r);
  }
  const label = recursionPlaceholderLabelEntity(blockName, sz, insertion, {
    id: `${baseId}-label`,
    spaceId,
  });
  const labelInsertionWithZ = {
    x: insertion.x,
    y: insertion.y,
    z: insertion.z,
  };
  const labelRenderable = entityToRenderableTyped(
    { ...label, insertionPoint: labelInsertionWithZ },
    ctx,
  );
  if (labelRenderable) primitives.push(labelRenderable);
  return primitives;
}
